import { lazy, memo, Suspense, useEffect, useRef, useState } from 'react';
import { AlertsPage } from './alerts/AlertsPage';
import { badgeSeverity } from './alerts/format';
import { isEnabled, shouldNotify, showNotification } from './alerts/notify';
import {
  type Access,
  canSeeService,
  routeAllowed,
  routeServices,
  selectableServices,
  useAccess,
  visibleDiagnoseSubs,
  visibleTabs,
} from './auth/access';
import { LoginPage } from './auth/LoginPage';
import { checkSession, logout, sessionStore, useSession } from './auth/useSession';
import { DetailPage } from './detail/DetailPage';
import { DiagnosePage } from './diagnose/DiagnosePage';
import { LivePage } from './live/LivePage';
import { ProfileDetailPage } from './profiles/ProfileDetailPage';
import { RequestsPage } from './requests/RequestsPage';
import {
  ADMIN_SUBS,
  type AdminSub,
  DIAGNOSE_SUBS,
  type DiagnoseSub,
  isDashboard,
  navigate,
  pageKey,
  type Route,
  replaceRoute,
  routeHref,
  TAB_NAMES,
  type TabName,
  tabOf,
  useRoute,
} from './router';
import { ServiceProvider, useServices } from './services/ServiceProvider';
import { ServicesPage } from './services/ServicesPage';
import { alertsForService, selectionSummary } from './services/selection';
import {
  promoteLegacyServiceFilter,
  setServiceFilter,
  useServiceFilter,
} from './services/service-filter';
import { serviceOptions } from './services/summary';
import type { StreamStatus } from './store/stream';
import { useLiveSelector, useStreamConnection } from './store/useStream';
import { ACCESS_TEXT, AUTH_TEXT, SUB_TAB_LABEL, TAB_LABEL } from './terms';
import { formatCount } from './time';
import { Button, EmptyState, ErrorBoundary, Field, Loading, Page, Select } from './ui';

/** 메뉴 여섯과 상세 둘. 라우팅은 `router.ts`의 해시 하나로 끝난다 (7~22-B회차) */
const TABS: readonly TabName[] = TAB_NAMES;

/** 메뉴별 첫 주소. 진단·관리는 첫 하위 탭으로 간다 */
const TAB_ROUTE: Record<TabName, Route> = {
  live: { name: 'live' },
  requests: { name: 'requests' },
  stats: { name: 'stats' },
  diagnose: { name: 'diagnose', sub: 'errors' },
  alerts: { name: 'alerts' },
  admin: { name: 'admin', sub: 'storage' },
};

/**
 * 관리·통계는 따로 떼어 처음 열 때 받는다 (D-C9). 관리(저장소·외부 연동·관제 서버)는 운영자만 가끔
 * 열고 통계는 차트가 무겁다. 첫 화면(서비스 목록·실시간)을 여는 사람은 두 묶음을 받지 않는다
 */
const LazyStatsPage = lazy(() =>
  import('./stats/StatsPage').then((module) => ({ default: module.StatsPage })),
);
const LazyAdminPage = lazy(() =>
  import('./admin/AdminPage').then((module) => ({ default: module.AdminPage })),
);

/**
 * 화면들. 머리는 경보·연결 상태가 바뀔 때 다시 그려지는데, 그때 화면 전체가 따라 그려지지 않게
 * `memo`로 감싼다 (D-C1). 화면이 받는 값은 주소에서 온 문자열·숫자뿐이라 얕은 비교로 충분하다
 */
const MemoServicesPage = memo(ServicesPage);
const MemoLivePage = memo(LivePage);
const MemoRequestsPage = memo(RequestsPage);
const MemoStatsPage = memo(function StatsRoute({ query }: { query?: string }) {
  return (
    <Suspense fallback={<Loading />}>
      <LazyStatsPage query={query} />
    </Suspense>
  );
});
const MemoDiagnosePage = memo(DiagnosePage);
const MemoAlertsPage = memo(AlertsPage);
const MemoAdminPage = memo(function AdminRoute({ sub }: { sub: AdminSub }) {
  return (
    <Suspense fallback={<Loading />}>
      <LazyAdminPage sub={sub} />
    </Suspense>
  );
});
const MemoDetailPage = memo(DetailPage);
const MemoProfileDetailPage = memo(ProfileDetailPage);

/** 헤더 배지 색. 경보 중인 것 중 가장 급한 심각도를 따른다 (10회차 결정 5) */
const BADGE_STYLE = {
  critical: 'bg-error/10 text-error ring-1 ring-inset ring-error/30',
  warning: 'bg-fail/10 text-fail ring-1 ring-inset ring-fail/30',
} as const;

/** 브라우저 알림을 띄운 경보 id 를 몇 개까지 기억할지 */
const NOTIFIED_LIMIT = 500;

const STATUS_LABEL: Record<StreamStatus, string> = {
  idle: '끊김',
  connecting: '연결 중',
  open: '실시간 연결됨',
  error: '연결 끊김, 다시 연결 중',
};

const STATUS_TEXT: Record<StreamStatus, string> = {
  idle: 'text-muted',
  connecting: 'text-fail',
  open: 'text-ok',
  error: 'text-error',
};

const STATUS_DOT: Record<StreamStatus, string> = {
  idle: 'bg-muted',
  connecting: 'bg-fail',
  open: 'bg-ok',
  error: 'bg-error',
};

export function App() {
  // 구형 링크의 해시 service 조건은 자식이 주소를 읽기 전에 전역 자리로 한 번 옮긴다.
  promoteLegacyServiceFilter();
  // 마지막 그물. 머리까지 죽는 드문 경우에도 하얀 화면 대신 안내가 뜬다 (44회차)
  return (
    <ErrorBoundary where="앱">
      <SessionGate />
    </ErrorBoundary>
  );
}

/**
 * 로그인 문 (51회차).
 *
 * **세션 확인이 먼저다.** 50회차까지는 `ServiceProvider`와 실시간 연결이 화면을 여는 즉시 돌았다.
 * 이제 둘 다 이 문 안쪽이라 로그인하기 전에는 어떤 조회도 부르지 않는다. 확인하는 동안에는 로그인
 * 화면을 그리지 않는다 — 로그인한 사람이 새로고침할 때마다 로그인 화면이 한 번 번쩍이면 안 된다.
 * 주소(해시)는 건드리지 않으므로 로그인하면 보던 화면으로 그대로 들어간다.
 */
function SessionGate() {
  const session = useSession();

  useEffect(() => {
    if (sessionStore.getState().status === 'checking') void checkSession();
  }, []);

  if (session.status === 'checking') {
    return (
      <main className="min-h-screen bg-canvas px-4 py-6 text-ink sm:px-8">
        <div className="mx-auto max-w-7xl">
          <Loading label={AUTH_TEXT.checking} />
        </div>
      </main>
    );
  }
  if (session.status === 'signedOut' || session.status === 'unreachable') {
    return <LoginPage notice={session.error} logoutFailed={session.logoutFailed === true} />;
  }
  return (
    <ServiceProvider>
      <AppBody key={session.session?.username ?? ''} account={session.session?.username} />
    </ServiceProvider>
  );
}

function AppBody({ account }: { account?: string }) {
  const route = useRoute();
  useStreamConnection();
  // 상태 전체가 아니라 머리가 쓰는 칸만 본다 (D-C1). 전체를 받으면 스냅샷 한 장마다 머리와
  // 화면 전체가 다시 그려졌다. 화면들은 아래 `memo`로 감싸 머리가 다시 그려져도 따라 그리지 않는다
  const alerts = useLiveSelector((live) => live.alerts);
  const events = useLiveSelector((live) => live.alertEvents);
  const status = useLiveSelector((live) => live.status);
  const service = useServiceFilter();
  const services = useServices();
  const hasServices = services.data !== undefined;
  // 서비스 목록을 받은 뒤에는 머리 숫자가 그 목록에서 온다. 그때는 스토어의 숫자를 안 본다(0 고정)
  const liveInstances = useLiveSelector((live) => (hasServices ? 0 : live.instances.length));
  const liveRequests = useLiveSelector((live) => (hasServices ? 0 : live.txCount));
  const visibleAlerts = alertsForService(alerts, service);
  const badge = badgeSeverity(visibleAlerts);
  const header = selectionSummary(services.data?.rows ?? [], service);
  const instanceCount = services.data ? header.instances : liveInstances;
  const requestCount = services.data ? header.requests : liveRequests;
  // 서비스를 고르기 전 화면에서는 선택기·숫자·탭이 가리킬 서비스가 없다 (32회차)
  const dashboard = isDashboard(route);
  const checkedInitialRoute = useRef(false);
  // 계정의 볼 범위 (52회차). 판정은 서버가 하고 여기는 메뉴·선택기를 그 판정에 맞춰 그릴 뿐이다
  const access = useAccess();
  const allowed = routeAllowed(access, route);
  /**
   * 이 화면이 부를 조회에 실릴 서비스 중 허용 밖인 것. 전역 선택(`?service=`)과 상세 주소에 박힌 서비스
   * (`svc=`)를 함께 본다 (리뷰 R52-B-5). 있으면 화면을 그리지 않는다 — 그리면 효과가 옮기기 전에 그
   * 서비스로 조회를 불러 403을 받는다
   */
  const foreignService = [service, ...routeServices(route)].find(
    (name) => !canSeeService(access, name),
  );
  const showPage = allowed && foreignService === undefined;
  /** 서비스가 하나뿐인 계정. 고를 것이 없다 */
  const only = !access.admin && access.services.length === 1 ? access.services[0] : undefined;
  const [notice, setNotice] = useState<string>();

  // 첫 진입에 서비스가 없으면 목록에서 고르게 한다. 공유 주소인 상세·CPU 기록·알림은 예외다.
  // 서비스가 하나뿐인 계정은 고를 것이 없어 목록을 거치지 않는다(아래 효과가 그 서비스로 옮긴다)
  useEffect(() => {
    if (checkedInitialRoute.current) return;
    checkedInitialRoute.current = true;
    if (
      only === undefined &&
      service === '' &&
      route.name !== 'services' &&
      route.name !== 'detail' &&
      route.name !== 'profile' &&
      route.name !== 'alerts'
    ) {
      replaceRoute({ name: 'services' });
    }
  }, [route, service, only]);

  // 허용 밖 서비스를 가리키는 옛 링크·북마크로 들어왔다. 서버는 그 조회를 403으로 거절하므로 화면을
  // 403 문구로 채우지 않고, 까닭을 적은 뒤 서비스 목록으로 보낸다 (52회차)
  useEffect(() => {
    if (foreignService === undefined) return;
    setNotice(ACCESS_TEXT.foreignService(foreignService));
    if (!canSeeService(access, service)) setServiceFilter('', true);
    replaceRoute({ name: 'services' });
  }, [access, service, foreignService]);

  // 서비스가 하나뿐인 계정은 그 서비스로 바로 간다. 허용 밖 링크로 들어온 직후에는 안내를 읽도록 두고,
  // 이미 고른 서비스가 있으면 건드리지 않는다 (52회차)
  useEffect(() => {
    if (only === undefined || notice !== undefined || service !== '') return;
    setServiceFilter(only, true);
    if (route.name === 'services') replaceRoute({ name: 'live' });
  }, [only, notice, service, route]);

  // 브라우저 알림은 사용자가 켠 경우에만 뜬다 (알림 탭의 토글).
  // 활성 경보뿐 아니라 사라짐·서버 오류 통보도 본다. 한 프레임의 여러 사건과 재연결을 구분한다.
  const notified = useRef(new Set<string>());
  useEffect(() => {
    const enabled = isEnabled();
    for (const alert of events) {
      if (notified.current.has(alert.id)) continue;
      // 꺼져 있거나 다른 서비스를 보는 동안 받은 사건도 소비한다. 나중에 선택해 옛 경보를 띄우지 않는다.
      notified.current.add(alert.id);
      const permitted = alert.service ? canSeeService(access, alert.service) : access.admin;
      const selected = !alert.service || service === '' || alert.service === service;
      if (permitted && selected && shouldNotify(alert, enabled)) showNotification(alert);
    }
    // 이 화면이 열려 있는 동안만 기억한다. 넘치면 오래 기억한 것부터 버린다
    for (const id of notified.current) {
      if (notified.current.size <= NOTIFIED_LIMIT) break;
      notified.current.delete(id);
    }
  }, [events, access, service]);

  return (
    <main className="min-h-screen bg-canvas px-4 py-6 text-ink sm:px-8">
      <div className="mx-auto max-w-7xl">
        {/*
          머리: 제목 왼쪽, 연결 상태·건수 오른쪽. 18회차까지의 배치에서 위의 영어 라벨만 뺐다.
          대시보드에서는 제목과 연결 상태만 남는다 (32회차) — 선택기·숫자·탭은 서비스를 고른 뒤에 뜻이 있다
        */}
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <h1 className="text-2xl font-semibold tracking-tight">
              <a href={routeHref({ name: 'services' })} className="text-ink no-underline">
                로그감시
              </a>
            </h1>
            {dashboard ? null : (
              <Field label="서비스" htmlFor="global-service">
                <Select
                  id="global-service"
                  value={service}
                  onChange={setServiceFilter}
                  options={serviceOptions(
                    services.data?.rows ?? [],
                    service,
                    access.admin ? undefined : selectableServices(access, []),
                  )}
                />
              </Field>
            )}
          </div>
          <div className="tabular flex flex-wrap items-center gap-4 text-sm text-muted">
            {!dashboard && badge !== undefined ? (
              <a
                href={routeHref({ name: 'alerts' })}
                className={`rounded-full px-3 py-1 text-xs font-semibold no-underline ${BADGE_STYLE[badge]}`}
              >
                경보 {visibleAlerts.length}건
              </a>
            ) : null}
            {dashboard ? null : (
              <>
                <span>인스턴스 {formatCount(instanceCount)}개</span>
                <span>최근 5분 완료 요청 {formatCount(requestCount)}건</span>
              </>
            )}
            <span
              aria-live="polite"
              className={`inline-flex items-center gap-2 ${STATUS_TEXT[status]}`}
            >
              <i aria-hidden="true" className={`size-2 rounded-full ${STATUS_DOT[status]}`} />
              {STATUS_LABEL[status]}
            </span>
            {/* 로그인이 꺼진 서버(`disabled`)에는 계정이 없어 둘 다 그리지 않는다 (51회차) */}
            {account !== undefined ? (
              <span className="inline-flex items-center gap-2">
                <span className="text-ink">{account}</span>
                <Button size="sm" onClick={() => void logout()}>
                  {AUTH_TEXT.logout}
                </Button>
              </span>
            ) : null}
          </div>
        </header>

        {dashboard ? null : (
          <>
            <Tabs current={tabOf(route)} tabs={visibleTabs(access, TABS)} />
            <SubTabs route={route} access={access} />
          </>
        )}

        {notice !== undefined ? (
          <div
            role="status"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-fail/40 bg-fail/5 px-4 py-3 text-sm text-fail"
          >
            <span>{notice}</span>
            <Button size="sm" onClick={() => setNotice(undefined)}>
              {ACCESS_TEXT.dismiss}
            </Button>
          </div>
        ) : null}

        {/* 화면 하나가 렌더 중에 던져도 머리·탭은 남아 다른 화면으로 옮길 수 있다 (44회차) */}
        <ErrorBoundary resetKey={pageKey(route)} where={pageKey(route)}>
          {/* 감춘 화면의 주소를 직접 쳤다. 그 화면의 조회를 부르지 않고 안내만 낸다 (52회차) */}
          {allowed ? null : <AdminOnly />}
          {showPage && route.name === 'services' ? <MemoServicesPage /> : null}
          {showPage && route.name === 'live' ? <MemoLivePage /> : null}
          {showPage && route.name === 'requests' ? <MemoRequestsPage query={route.query} /> : null}
          {showPage && route.name === 'stats' ? <MemoStatsPage query={route.query} /> : null}
          {showPage && route.name === 'diagnose' ? (
            <MemoDiagnosePage sub={route.sub} query={route.query} />
          ) : null}
          {showPage && route.name === 'alerts' ? <MemoAlertsPage /> : null}
          {showPage && route.name === 'admin' ? <MemoAdminPage sub={route.sub} /> : null}
          {showPage && route.name === 'detail' ? (
            <MemoDetailPage
              traceId={route.traceId}
              from={route.from}
              back={route.back}
              service={route.service}
              txId={route.txId}
              at={route.at}
            />
          ) : null}
          {showPage && route.name === 'profile' ? <MemoProfileDetailPage id={route.id} /> : null}
        </ErrorBoundary>
      </div>
    </main>
  );
}

/**
 * 감춘 화면 자리 (52회차). 일반 계정이 관리 메뉴나 진단의 DB 감시 주소를 직접 쳤을 때 낸다.
 * 서버도 그 조회를 403으로 거절한다 — 여기서 안 부르는 것은 화면을 403 문구로 채우지 않기 위해서다
 */
function AdminOnly() {
  return (
    <Page>
      <EmptyState title={ACCESS_TEXT.adminOnlyTitle} hint={ACCESS_TEXT.adminOnlyHint} />
    </Page>
  );
}

/** 탭 묶음 안에서 화살표·Home·End로 옮긴다. 옮겼으면 참 */
function moveFocus(
  refs: readonly (HTMLAnchorElement | null)[],
  index: number,
  key: string,
): boolean {
  const last = refs.length - 1;
  let next = index;
  if (key === 'ArrowRight') next = index === last ? 0 : index + 1;
  else if (key === 'ArrowLeft') next = index === 0 ? last : index - 1;
  else if (key === 'Home') next = 0;
  else if (key === 'End') next = last;
  else return false;
  refs[next]?.focus();
  return true;
}

/**
 * 위 줄 탭 (콘솔 검토 D-2).
 *
 * 16회차까지 `<nav>` 안의 `<button>` 아홉이고 지금 어느 탭인지는 배경색으로만 알렸다.
 * `role="tablist"`를 붙이고 좌우 화살표·Home·End로도 옮길 수 있다.
 * 모양은 18회차까지의 상자형 탭 바 그대로다 — 지금 탭은 채움색과 굵기, aria로 알린다.
 * 22-B회차에 열 개가 여섯으로 줄고 진단·관리는 아래 줄(`SubTabs`)로 나뉜다.
 */
function Tabs({ current, tabs }: { current: TabName | undefined; tabs: readonly TabName[] }) {
  const refs = useRef<(HTMLAnchorElement | null)[]>([]);

  return (
    <nav aria-label="화면" className="mb-3">
      {/* 탭 묶음은 `role="tablist"`로 알린다. `<nav>` 자체에 붙이면 뜻이 겹친다 */}
      <div
        role="tablist"
        className="flex gap-1 overflow-x-auto rounded-xl border border-hair bg-band p-1"
      >
        {tabs.map((tab, index) => {
          const active = current === tab;
          return (
            <a
              key={tab}
              ref={(node) => {
                refs.current[index] = node;
              }}
              href={routeHref(TAB_ROUTE[tab])}
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              tabIndex={active ? 0 : -1}
              onKeyDown={(event) => {
                if (moveFocus(refs.current, index, event.key)) event.preventDefault();
              }}
              onClick={(event) => {
                // 해시 링크라 브라우저가 알아서 옮기지만, 같은 주소를 눌러도 반응하게 한다
                event.preventDefault();
                navigate(TAB_ROUTE[tab]);
              }}
              className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium no-underline transition-colors duration-150 ${
                active ? 'bg-accent text-white' : 'text-muted hover:bg-band-strong hover:text-ink'
              }`}
            >
              {TAB_LABEL[tab]}
            </a>
          );
        })}
      </div>
    </nav>
  );
}

/** 하위 탭 하나. 라우트와 이름 */
interface SubTab {
  key: string;
  route: Route;
  label: string;
}

/** 지금 탭에 딸린 작은 탭들. 진단·관리에만 있다 */
function subTabsOf(
  route: Route,
  access: Access,
): { label: string; current: string; tabs: SubTab[] } | undefined {
  const tab = tabOf(route);
  if (tab === 'diagnose') {
    // 상세도 그것을 연 하위 탭을 켠 채로 둔다
    let current: DiagnoseSub = 'db';
    if (route.name === 'diagnose') current = route.sub;
    else if (route.name === 'profile') current = 'cpu';
    else if (route.name === 'detail' && route.from === 'errors') current = 'errors';
    return {
      label: '진단 하위',
      current,
      // DB 감시는 관리자에게만 낸다 (52회차)
      tabs: visibleDiagnoseSubs(access, DIAGNOSE_SUBS).map((sub) => ({
        key: sub,
        route: { name: 'diagnose', sub },
        label: SUB_TAB_LABEL[sub],
      })),
    };
  }
  if (tab === 'admin' && route.name === 'admin') {
    return {
      label: '관리 하위',
      current: route.sub,
      tabs: ADMIN_SUBS.map((sub) => ({
        key: sub,
        route: { name: 'admin', sub },
        label: SUB_TAB_LABEL[sub],
      })),
    };
  }
  return undefined;
}

/**
 * 둘째 줄의 작은 탭 (22-B회차). 위 줄과 같은 키보드 규칙이고 모양만 작다.
 * 하위 탭이 없는 메뉴에서는 줄 자체가 없다. 빈 줄이 남으면 화면이 아래로 밀린다
 */
function SubTabs({ route, access }: { route: Route; access: Access }) {
  const refs = useRef<(HTMLAnchorElement | null)[]>([]);
  const group = subTabsOf(route, access);

  if (group === undefined) return <div className="mb-3" />;
  return (
    <nav aria-label={group.label} className="mb-6">
      {/* 단추 묶음(segmented)으로 그린다. 켜진 것은 파랑 채움, 나머지는 흰 단추에 세로 구분선. 셋뿐이라 스크롤은 없다 */}
      <div role="tablist" className="inline-flex overflow-hidden rounded-lg border border-line">
        {group.tabs.map((tab, index) => {
          const active = group.current === tab.key;
          return (
            <a
              key={tab.key}
              ref={(node) => {
                refs.current[index] = node;
              }}
              href={routeHref(tab.route)}
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              tabIndex={active ? 0 : -1}
              onKeyDown={(event) => {
                if (moveFocus(refs.current, index, event.key)) event.preventDefault();
              }}
              onClick={(event) => {
                event.preventDefault();
                navigate(tab.route);
              }}
              className={`whitespace-nowrap border-r border-line px-4 py-1.5 text-sm no-underline transition-colors duration-150 last:border-r-0 ${
                active ? 'bg-accent font-medium text-white' : 'bg-panel text-ink hover:bg-band'
              }`}
            >
              {tab.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
